// Apresentação compartilhada de contato nos pickers do CRM.
//
// Existia duplicado em NewDealSheet (combobox do contato principal) e em
// CrmDealDetail (picker de participantes), cada um mostrando só o órgão —
// o que deixava indistinguíveis contatos de órgãos homônimos em cidades
// diferentes ("Prefeitura Municipal" em Cuiabá e em Várzea Grande).
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Landmark,
  Building2,
  User as UserIcon,
  MapPin,
  Briefcase,
} from "lucide-react";

/**
 * Forma mínima de contato usada pelos pickers.
 *
 * `city`, `state` e `sector` NÃO são campos novos: `getContacts` faz
 * `db.select()` de todas as colunas e `contacts.list` já os devolvia — só não
 * estavam declarados aqui nem renderizados.
 */
export type ContactLite = {
  id: number;
  name: string;
  lastName?: string | null;
  companyName?: string | null;
  contactType?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  sector?: string | null;
  city?: string | null;
  state?: string | null;
  /** Cargo. Existe a partir da migration 0089; antes disso vem undefined. */
  jobTitle?: string | null;
};

export function contactInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const CONTACT_TYPE_CFG: Record<
  string,
  { icon: any; color: string; label: string }
> = {
  public_admin: {
    icon: Landmark,
    color: "text-amber-700 dark:text-amber-300",
    label: "B2G · Órgão",
  },
  empresa: {
    icon: Building2,
    color: "text-sky-700 dark:text-sky-300",
    label: "B2B · Empresa",
  },
  cliente_final: {
    icon: UserIcon,
    color: "text-emerald-700 dark:text-emerald-300",
    label: "B2C · Pessoa",
  },
};

export function contactTypeCfg(contactType?: string | null) {
  return CONTACT_TYPE_CFG[contactType ?? "empresa"] ?? CONTACT_TYPE_CFG.empresa;
}

export function contactFullName(c: {
  name?: string | null;
  lastName?: string | null;
}): string {
  return [c.name, c.lastName].filter(Boolean).join(" ");
}

/**
 * Linha de resultado do picker: nome / órgão / cidade · cargo.
 *
 * A cidade é `shrink-0` de propósito — é ela que desambigua órgãos homônimos,
 * então nunca pode ser o pedaço que some no truncate. Quem trunca é o órgão
 * (campo longo e repetitivo) e o cargo.
 */
export function ContactResultRow({ c }: { c: ContactLite }) {
  const cfg = contactTypeCfg(c.contactType);
  const TypeIcon = cfg.icon;
  const full = contactFullName(c);
  const location = [c.city, c.state].filter(Boolean).join("/");
  // jobTitle só existe depois da 0089; até lá o setor ocupa a linha.
  const role = c.jobTitle || c.sector || null;

  return (
    <div className="flex items-start gap-2 min-w-0 w-full">
      <Avatar className="h-7 w-7 shrink-0 mt-0.5">
        <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
          {contactInitials(full)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0 leading-tight">
        <div className="text-sm font-medium truncate">{full}</div>

        <div className="text-xs text-muted-foreground flex items-center gap-1 min-w-0">
          <TypeIcon className={`h-3 w-3 shrink-0 ${cfg.color}`} />
          <span className="truncate">{c.companyName ?? cfg.label}</span>
        </div>

        {(location || role) && (
          <div className="text-[11px] text-muted-foreground/80 flex items-center gap-1.5 min-w-0 mt-0.5">
            {location && (
              <span className="inline-flex items-center gap-1 shrink-0">
                <MapPin className="h-3 w-3" />
                {location}
              </span>
            )}
            {location && role && <span className="opacity-40">·</span>}
            {role && (
              <span className="inline-flex items-center gap-1 min-w-0">
                <Briefcase className="h-3 w-3 shrink-0" />
                <span className="truncate">{role}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
